import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Search, Edit3, Trash2, Power, Star, Loader2, RefreshCw, X,
  CheckCircle2, AlertCircle, DollarSign, Clock, Phone, MapPin, Tag,
  Save, Sparkles, Eye, EyeOff,
} from "lucide-react";
import { authFetch } from "../utils/adminAuth";
import AdminShell, { StatCard, StatusPill } from "../components/admin/AdminShell";
import {
  OFFER_ICONS, OFFER_ICON_NAMES, getOfferIcon, COLOR_PRESETS,
  TRAFFIC_SOURCES, OFFER_CATEGORIES, PAYMENT_TERMS,
} from "../utils/offerIcons";

const EMPTY_OFFER = {
  name: "",
  category: "Home Services",
  icon: "Phone",
  color: "from-blue-500 to-cyan-500",
  payout: "",
  is_fixed: false,
  buffer_time: "120",
  payment_terms: "Net 10",
  call_type: "",
  country: "USA",
  working_hours: "Mon-Sun, 8am-10pm Local",
  description: "",
  restrictions: "No fraud, fake, massage, spa or IVR-manipulated calls.",
  traffic_sources: [],
  zipcode_sheet_id: "",
  popular: false,
  status: "active",
  sort_order: 100,
};

// --------------------------------------------------------------------------- //
// Main page                                                                   //
// --------------------------------------------------------------------------- //

export default function AdminOffersPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | active | paused
  const [editing, setEditing] = useState(null); // null | "new" | offer
  const [toast, setToast] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const resp = await authFetch("/api/admin/offers");
      if (resp.ok) setOffers(await resp.json());
      else showToast("error", "Failed to load offers");
    } catch (e) {
      showToast("error", `Network error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []);

  const filtered = useMemo(() => {
    return offers.filter((o) => {
      const matchesSearch =
        !search ||
        o.name?.toLowerCase().includes(search.toLowerCase()) ||
        o.category?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = filterStatus === "all" || o.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [offers, search, filterStatus]);

  const counts = useMemo(() => ({
    total: offers.length,
    active: offers.filter((o) => o.status === "active").length,
    paused: offers.filter((o) => o.status === "paused").length,
    featured: offers.filter((o) => o.popular).length,
  }), [offers]);

  // --- handlers --- //

  const handleSave = async (payload) => {
    const isNew = !payload.id;
    const url = isNew
      ? "/api/admin/offers"
      : `/api/admin/offers/${payload.id}`;
    const method = isNew ? "POST" : "PATCH";
    const { id, created_at, updated_at, ...body } = payload;
    try {
      const resp = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        showToast("error", `Save failed: ${txt}`);
        return false;
      }
      showToast("success", isNew ? "Offer created" : "Offer updated");
      setEditing(null);
      await fetchOffers();
      return true;
    } catch (e) {
      showToast("error", `Network error: ${e.message}`);
      return false;
    }
  };

  const handleToggle = async (offer) => {
    const resp = await authFetch(`/api/admin/offers/${offer.id}/toggle`, { method: "POST" });
    if (resp.ok) {
      showToast("success", `Offer ${offer.status === "active" ? "paused" : "activated"}`);
      fetchOffers();
    } else {
      showToast("error", "Toggle failed");
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(`Permanently delete "${offer.name}"? This cannot be undone.`)) return;
    const resp = await authFetch(`/api/admin/offers/${offer.id}`, { method: "DELETE" });
    if (resp.ok) {
      showToast("success", "Offer deleted");
      fetchOffers();
    } else {
      showToast("error", "Delete failed");
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("Import the 22 original hardcoded offers into the database? Existing offers with the same name will be skipped.")) return;
    setSeeding(true);
    try {
      const resp = await authFetch("/api/admin/offers/seed-defaults", { method: "POST" });
      if (resp.ok) {
        const j = await resp.json();
        showToast("success", `Seeded ${j.inserted} new offers (${j.skipped} skipped).`);
        fetchOffers();
      } else {
        showToast("error", "Seed failed");
      }
    } finally {
      setSeeding(false);
    }
  };

  return (
    <AdminShell
      pageTitle="Offers"
      pageDescription="Manage live offers shown on your public /offers page."
      rightSlot={
        <button
          onClick={fetchOffers}
          data-testid="offers-refresh-btn"
          className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2.25} />
          Refresh
        </button>
      }
    >
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-20 right-6 z-[80] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border ${
            toast.kind === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
          data-testid="offers-toast"
        >
          {toast.kind === "success" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span className="text-sm font-medium">{toast.msg}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total offers" value={counts.total} icon={Tag} testId="stat-total-offers" />
        <StatCard label="Active" value={counts.active} icon={CheckCircle2} tone="success" testId="stat-active-offers" />
        <StatCard label="Paused" value={counts.paused} icon={EyeOff} tone="warning" testId="stat-paused-offers" />
        <StatCard label="Featured (HOT)" value={counts.featured} icon={Star} tone="violet" testId="stat-featured-offers" />
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex flex-1 gap-3 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="offers-search"
              className="w-full pl-9 pr-3 h-9 text-sm border border-slate-200 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="offers-status-filter"
            className="h-9 text-sm border border-slate-200 rounded-md px-3 focus:border-blue-500 outline-none"
          >
            <option value="all">All statuses</option>
            <option value="active">Active only</option>
            <option value="paused">Paused only</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSeed}
            disabled={seeding}
            data-testid="offers-seed-btn"
            title="Re-import the 22 default offers (skips any with a matching name)"
            className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 hover:border-amber-500/60 hover:text-amber-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} />
            {seeding ? "Importing..." : "Import default offers"}
          </button>
          <button
            onClick={() => setEditing("new")}
            data-testid="offers-new-btn"
            className="flex items-center gap-1.5 px-4 h-9 rounded-md text-[13px] font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg shadow-blue-500/20 transition-all"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            New Offer
          </button>
        </div>
      </div>

      {/* Table / list */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-slate-500">Loading offers...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-900 mb-1">No offers yet</h3>
          <p className="text-sm text-slate-500 mb-4">
            {offers.length === 0
              ? "Click 'Import 22 default offers' to migrate the existing list, or create a new one."
              : "No offers match your filters."}
          </p>
        </div>
      ) : (
        <OffersTable
          offers={filtered}
          onEdit={(o) => setEditing(o)}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />
      )}

      {/* Editor drawer */}
      {editing && (
        <OfferEditor
          initial={editing === "new" ? EMPTY_OFFER : editing}
          isNew={editing === "new"}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </AdminShell>
  );
}

// --------------------------------------------------------------------------- //
// Offers table                                                                //
// --------------------------------------------------------------------------- //

function OffersTable({ offers, onEdit, onToggle, onDelete }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="offers-table">
          <thead className="bg-slate-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Offer</th>
              <th className="px-4 py-3 text-left font-semibold">Category</th>
              <th className="px-4 py-3 text-left font-semibold">Payout</th>
              <th className="px-4 py-3 text-left font-semibold">Buffer</th>
              <th className="px-4 py-3 text-left font-semibold">Terms</th>
              <th className="px-4 py-3 text-left font-semibold">Traffic</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => {
              const Icon = getOfferIcon(o.icon);
              return (
                <tr
                  key={o.id}
                  className="border-b border-gray-100 hover:bg-slate-50/60 transition-colors"
                  data-testid={`offer-row-${o.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${o.color} flex items-center justify-center shadow-sm`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                          {o.name}
                          {o.popular && (
                            <span className="text-[10px] font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white px-1.5 py-0.5 rounded">HOT</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{o.call_type || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{o.category}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {o.is_fixed ? "" : "Up to "}${o.payout}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{o.buffer_time}s</td>
                  <td className="px-4 py-3 text-slate-700">{o.payment_terms}</td>
                  <td className="px-4 py-3">
                    {o.traffic_sources?.length ? (
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {o.traffic_sources.map((t) => (
                          <span key={t} className="text-[10px] font-medium bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {o.status === "active" ? (
                      <StatusPill tone="emerald" label="Active" />
                    ) : (
                      <StatusPill tone="amber" label="Paused" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => onToggle(o)}
                        title={o.status === "active" ? "Pause offer" : "Activate offer"}
                        data-testid={`offer-toggle-${o.id}`}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEdit(o)}
                        title="Edit"
                        data-testid={`offer-edit-${o.id}`}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-700"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(o)}
                        title="Delete"
                        data-testid={`offer-delete-${o.id}`}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Editor drawer                                                               //
// --------------------------------------------------------------------------- //

function OfferEditor({ initial, isNew, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSource = (src) => {
    setForm((f) => {
      const has = f.traffic_sources.includes(src);
      return {
        ...f,
        traffic_sources: has
          ? f.traffic_sources.filter((s) => s !== src)
          : [...f.traffic_sources, src],
      };
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      alert("Offer name is required.");
      return;
    }
    if (!form.payout.toString().trim()) {
      alert("Payout is required.");
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const PreviewIcon = getOfferIcon(form.icon);

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-stretch justify-end" data-testid="offer-editor-modal">
      <div className="w-full max-w-3xl bg-white shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">
              {isNew ? "Create offer" : `Edit: ${initial.name}`}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Saved offers appear instantly on the public /offers page.
            </p>
          </div>
          <button onClick={onCancel} className="p-2 rounded-md hover:bg-slate-100 text-slate-600" data-testid="offer-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview card */}
        <div className="px-6 pt-6">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${form.color} flex items-center justify-center shadow-md`}>
              <PreviewIcon className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-slate-900 flex items-center gap-2">
                {form.name || "Offer name..."}
                {form.popular && (
                  <span className="text-[10px] font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white px-1.5 py-0.5 rounded">HOT</span>
                )}
              </div>
              <div className="text-xs text-slate-500">{form.category}</div>
              <div className="mt-1 text-sm">
                <span className="text-slate-500">{form.is_fixed ? "Fixed: " : "Up to "}</span>
                <span className="font-bold text-emerald-700">${form.payout || "0"}</span>
                <span className="text-slate-400 ml-1">/ call</span>
              </div>
            </div>
            <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${form.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              {form.status}
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="px-6 py-6 space-y-6">
          {/* Basics */}
          <Section title="Basics">
            <Field label="Offer name *">
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                data-testid="offer-field-name"
                placeholder="e.g. HVAC Repair"
                className={fieldCls}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <select value={form.category} onChange={(e) => update("category", e.target.value)} className={fieldCls} data-testid="offer-field-category">
                  {OFFER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Call type">
                <input
                  type="text"
                  value={form.call_type}
                  onChange={(e) => update("call_type", e.target.value)}
                  placeholder="e.g. Repair & Installation"
                  className={fieldCls}
                  data-testid="offer-field-call-type"
                />
              </Field>
            </div>
            <Field label="Description (optional, shown in offer card)">
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                rows={2}
                placeholder="One-line value prop..."
                className={textareaCls}
                data-testid="offer-field-description"
              />
            </Field>
          </Section>

          {/* Payout */}
          <Section title="Payout & Terms">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Payout amount (USD) *">
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={form.payout}
                    onChange={(e) => update("payout", e.target.value)}
                    placeholder="94"
                    className={`${fieldCls} pl-9`}
                    data-testid="offer-field-payout"
                  />
                </div>
              </Field>
              <Field label="Fixed payout?">
                <label className="flex items-center gap-2 h-9 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.is_fixed}
                    onChange={(e) => update("is_fixed", e.target.checked)}
                    data-testid="offer-field-is-fixed"
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  Treat as fixed (no "Up to" prefix)
                </label>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Buffer time (sec)">
                <input
                  type="text"
                  value={form.buffer_time}
                  onChange={(e) => update("buffer_time", e.target.value)}
                  placeholder="150"
                  className={fieldCls}
                  data-testid="offer-field-buffer"
                />
              </Field>
              <Field label="Payment terms">
                <select value={form.payment_terms} onChange={(e) => update("payment_terms", e.target.value)} className={fieldCls} data-testid="offer-field-terms">
                  {PAYMENT_TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Country / GEO">
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                  placeholder="USA"
                  className={fieldCls}
                  data-testid="offer-field-country"
                />
              </Field>
            </div>
            <Field label="Working hours">
              <input
                type="text"
                value={form.working_hours}
                onChange={(e) => update("working_hours", e.target.value)}
                placeholder="Mon-Sun, 8am-10pm Local"
                className={fieldCls}
                data-testid="offer-field-hours"
              />
            </Field>
          </Section>

          {/* Traffic sources */}
          <Section title="Allowed Traffic Sources">
            <p className="text-xs text-slate-500 -mt-2 mb-2">
              Select every traffic source publishers may use to promote this offer.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TRAFFIC_SOURCES.map((src) => {
                const active = form.traffic_sources.includes(src);
                return (
                  <button
                    type="button"
                    key={src}
                    onClick={() => toggleSource(src)}
                    data-testid={`offer-traffic-${src.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`text-left text-xs font-medium px-3 py-2 rounded-md border transition-colors ${
                      active
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {active ? "✓ " : "+ "}{src}
                  </button>
                );
              })}
            </div>
            {/* Legacy / unknown sources — saved but not in the known list. Shown so admins can remove them. */}
            {(() => {
              const unknown = form.traffic_sources.filter((s) => !TRAFFIC_SOURCES.includes(s));
              if (unknown.length === 0) return null;
              return (
                <div className="mt-3 p-3 rounded-md border border-amber-200 bg-amber-50">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 mb-2">
                    Other (legacy) — click ✕ to remove
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {unknown.map((src) => (
                      <button
                        type="button"
                        key={src}
                        onClick={() => toggleSource(src)}
                        className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                        data-testid={`offer-traffic-legacy-${src.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {src}
                        <X className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Section>

          {/* Visual */}
          <Section title="Visual">
            <Field label="Icon">
              <div className="grid grid-cols-8 gap-2 max-h-40 overflow-y-auto p-2 border border-slate-200 rounded-md bg-slate-50">
                {OFFER_ICON_NAMES.map((name) => {
                  const Ic = OFFER_ICONS[name];
                  const active = form.icon === name;
                  return (
                    <button
                      type="button"
                      key={name}
                      onClick={() => update("icon", name)}
                      title={name}
                      className={`w-10 h-10 rounded-md flex items-center justify-center transition-colors ${
                        active ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                      }`}
                    >
                      <Ic className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Color gradient">
              <div className="grid grid-cols-6 gap-2">
                {COLOR_PRESETS.map((c) => {
                  const active = form.color === c;
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => update("color", c)}
                      className={`h-9 rounded-md bg-gradient-to-r ${c} ${active ? "ring-2 ring-offset-2 ring-slate-900" : ""}`}
                      title={c}
                    />
                  );
                })}
              </div>
            </Field>
          </Section>

          {/* Restrictions */}
          <Section title="Restrictions / Do's & Don'ts">
            <Field label="Restrictions">
              <textarea
                value={form.restrictions}
                onChange={(e) => update("restrictions", e.target.value)}
                rows={3}
                placeholder="e.g. No incentivized traffic, no spa/massage calls..."
                className={textareaCls}
                data-testid="offer-field-restrictions"
              />
            </Field>
            <Field label="ZIP code sheet ID (optional, Google Sheets)">
              <input
                type="text"
                value={form.zipcode_sheet_id || ""}
                onChange={(e) => update("zipcode_sheet_id", e.target.value)}
                placeholder="e.g. 1G9rBXpaMYDLmgvvhp-xxeA8Cp0_r1TLwxCP2U9ByQaA"
                className={fieldCls}
                data-testid="offer-field-zipsheet"
              />
            </Field>
          </Section>

          {/* Flags */}
          <Section title="Visibility">
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.popular}
                  onChange={(e) => update("popular", e.target.checked)}
                  data-testid="offer-field-popular"
                  className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400"
                />
                <Star className="w-4 h-4 text-amber-500" /> Mark as HOT / Popular
              </label>
              <Field label="Status">
                <select value={form.status} onChange={(e) => update("status", e.target.value)} className={fieldCls} data-testid="offer-field-status">
                  <option value="active">Active (visible publicly)</option>
                  <option value="paused">Paused (hidden)</option>
                </select>
              </Field>
              <Field label="Sort order (lower = first)">
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => update("sort_order", parseInt(e.target.value || "100", 10))}
                  className={fieldCls}
                  data-testid="offer-field-sort"
                />
              </Field>
            </div>
          </Section>

          {/* Footer actions */}
          <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 h-9 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="offer-editor-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="offer-editor-save"
              className="flex items-center gap-1.5 px-5 h-9 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isNew ? "Create offer" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Small UI primitives                                                         //
// --------------------------------------------------------------------------- //

const fieldCls =
  "w-full h-9 text-sm border border-slate-200 rounded-md px-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none bg-white";

const textareaCls =
  "w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none bg-white resize-y";

const Section = ({ title, children }) => (
  <section>
    <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">{title}</h3>
    <div className="space-y-3">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-medium text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);
